// Core packages
import { Injectable } from '@angular/core';

// Third party packages
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

// Custom packages
import { CookieService } from 'ngx-cookie-service';
import { ApiService } from 'src/app/shared/services/api.service';
import { HelperService } from 'src/app/shared/services/helper.service';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {

  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService,
    private cookieService: CookieService
  ) { }

  /**
   * Get structure setting data from back-end
   *
   * @since 1.0.0
   *
   */
  getStructureView(): Observable<any> {
    return this.apiService.apiCall('GET', 'condo-structures/settings/view').pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Get structure setting data from back-end
   *
   * @since 1.0.0
   *
   */
  getStructure(): Observable<any> {
    return this.apiService.apiCall('GET', 'condo-structures/settings').pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Update structure with given condoStructureId with given data
   *
   * @since 1.0.0
   *
   * @param condoStructureId ID of condo structure setting to update
   * @param data New condo structure setting data
   */
  updateStructure(condoStructureId: string, areaId: string, subAreaId: string, data: object): Observable<any> {
    if (typeof areaId !== 'undefined' && typeof subAreaId !== 'undefined') {
      return this.apiService.apiCall('PUT', `condo-structures/settings/${condoStructureId}/${areaId}/${subAreaId}`, data);
    } else if (typeof areaId !== 'undefined' && typeof subAreaId === 'undefined') {
      return this.apiService.apiCall('PUT', `condo-structures/settings/${condoStructureId}/${areaId}`, data);
    } else {
      return this.apiService.apiCall('PUT', `condo-structures/settings/${condoStructureId}`, data);
    }
  }
  
  /**
   * Create document with given documentId with given data
   *
   * @since 1.0.0
   *
   * @param documencondoStructureIdtId ID of document setting to update
   * @param data New document setting data
   */
   createStructure(condoStructureId: string, areaId: string, subAreaId: string, data: object): Observable<any> {
    if (typeof areaId !== 'undefined' && typeof subAreaId !== 'undefined') {
      return this.apiService.apiCall('POST', `condo-structures/settings/${condoStructureId}/${areaId}/${subAreaId}`, data);
    } else if (typeof areaId !== 'undefined' && typeof subAreaId === 'undefined') {
      return this.apiService.apiCall('POST', `condo-structures/settings/${condoStructureId}/${areaId}`, data);
    } else {
      return this.apiService.apiCall('POST', `condo-structures/settings/${condoStructureId}`, data);
    }
  }

  /**
   * Move a second-level content (section) between two areas of the same condo-structure template.
   * The moved content is appended at the end of the destination area, preserving its _id and sub-fields.
   *
   * @since 3.0.0
   *
   * @param condoStructureId Template id (same for source and destination)
   * @param sourceAreaId     Area currently containing the content (must differ from destAreaId)
   * @param destAreaId       Destination area
   * @param contentId        Content (section) to move
   */
  moveCondoStructureContent(
    condoStructureId: string,
    sourceAreaId: string,
    destAreaId: string,
    contentId: string
  ): Observable<any> {
    const data = { condoStructureId, sourceAreaId, destAreaId, contentId };
    // v2-only endpoint: force version regardless of the environment default
    return this.apiService.apiCall(
      'PUT',
      'condo-structures/settings/move/content',
      data,
      undefined,
      undefined,
      undefined,
      'v2'
    );
  }

  /**
   * Get document setting data from back-end
   *
   * @since 1.0.0
   *
   */
  getDocument(): Observable<any> {
    return this.apiService.apiCall('GET', 'document-archives/settings').pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Update document with given documentId with given data
   *
   * @since 1.0.0
   *
   * @param documentId ID of document setting to update
   * @param data New document setting data
   */
  updateDocument(documentId: string, areaId: string, subAreaId: string, data: object): Observable<any> {
    if (typeof areaId !== 'undefined' && typeof subAreaId !== 'undefined') {
      return this.apiService.apiCall('PUT', `document-archives/settings/${documentId}/${areaId}/${subAreaId}`, data);
    } else if (typeof areaId !== 'undefined' && typeof subAreaId === 'undefined') {
      return this.apiService.apiCall('PUT', `document-archives/settings/${documentId}/${areaId}`, data);
    } else {
      return this.apiService.apiCall('PUT', `document-archives/settings/${documentId}`, data);
    }
  }

  /**
   * Create document with given documentId with given data
   *
   * @since 1.0.0
   *
   * @param documentId ID of document setting to update
   * @param data New document setting data
   */
  createDocument(documentId: string, areaId: string, subAreaId: string, data: object): Observable<any> {
    if (typeof areaId !== 'undefined' && typeof subAreaId !== 'undefined') {
      return this.apiService.apiCall('POST', `document-archives/settings/${documentId}/${areaId}/${subAreaId}`, data);
    } else if (typeof areaId !== 'undefined' && typeof subAreaId === 'undefined') {
      return this.apiService.apiCall('POST', `document-archives/settings/${documentId}/${areaId}`, data);
    } else {
      return this.apiService.apiCall('POST', `document-archives/settings/${documentId}`, data);
    }
  }

  /**
   * Move a second-level content (section) between two areas of the same template.
   * The moved content is appended at the end of the destination area.
   *
   * @since 3.0.0
   *
   * @param documentId   Template id (same for source and destination)
   * @param sourceAreaId Area currently containing the content
   * @param destAreaId   Destination area (must differ from sourceAreaId)
   * @param contentId    Content to move
   */
  moveDocumentContent(
    documentId: string,
    sourceAreaId: string,
    destAreaId: string,
    contentId: string
  ): Observable<any> {
    const data = { documentId, sourceAreaId, destAreaId, contentId };
    // v2-only endpoint: force version regardless of the environment default
    return this.apiService.apiCall(
      'PUT',
      'document-archives/settings/move/content',
      data,
      undefined,
      undefined,
      undefined,
      'v2'
    );
  }

  /**
   * Move a third-level document between two contents (sections) of the same template.
   * Source and destination contents may belong to the same area or to different areas.
   * The moved document is appended at the end of the destination content.
   *
   * @since 3.0.0
   *
   * @param documentId      Template id (same for source and destination)
   * @param sourceAreaId    Area containing the source content
   * @param sourceContentId Content currently containing the document
   * @param destAreaId      Area containing the destination content
   * @param destContentId   Destination content (must differ from sourceContentId)
   * @param docId           Document to move
   */
  moveDocumentDocument(
    documentId: string,
    sourceAreaId: string,
    sourceContentId: string,
    destAreaId: string,
    destContentId: string,
    docId: string
  ): Observable<any> {
    const data = {
      documentId,
      sourceAreaId,
      sourceContentId,
      destAreaId,
      destContentId,
      docId
    };
    // v2-only endpoint: force version regardless of the environment default
    return this.apiService.apiCall(
      'PUT',
      'document-archives/settings/move/document',
      data,
      undefined,
      undefined,
      undefined,
      'v2'
    );
  }
}
